import { useContext, useEffect, useMemo, useState } from 'react';
import { Bookable, BookingDisplay } from '@/types';
import { formatDate } from '@/utils/formatDate';
import { formateTime } from '@/utils/formatTime';
import { getBookingDisplay } from '@/api/getBookingDisplay';
import { TDirection } from '../../data/direction';
import { getBookableDisplay } from '@/api/getBookableDisplay';
import { BookingSvgThemeContext, ThemePaletteContext } from '@/components/ThemeWrapper';
import { MIN_IMAGE_BAND, useBookingImageBand } from '@/hooks/use-booking-image-band';

function settingNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function svgLayout(fontSize: string | number, fallback: number) {
  const rawSize = typeof fontSize === 'number' ? fontSize : parseFloat(fontSize);
  const size = Number.isFinite(rawSize) ? rawSize : fallback;
  const height = Math.ceil(size * 1.5);
  return { viewBox: `0 0 1800 ${height}`, y: height / 2 };
}

interface BookingPageItemProps {
  bookable?: { bookableId: number; direction: TDirection };
  hideEndTime: boolean;
  timeThreshold: number;
  forcedTimeThreshold: number;
  hideName: boolean;
  hideUnbookedName?: boolean;
  hideExtra: boolean;
  hideTime: boolean;
  hideLocation: boolean;
  hideImage: boolean;
  inverted?: boolean;
  paddingLeft?: number;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  textAlignVertical?: 'top' | 'center' | 'bottom';
  textAlignHorizontal?: 'left' | 'center' | 'right';
}

// const positions = [
//   { id: 'name', x: 0, y: 0, w: 12, h: 1 },
//   {
//     id: 'extra',
//     x: 0,
//     y: 1,
//     w: 12,
//     h: 1,
//   },
//   {
//     id: 'location',
//     x: 0,
//     y: 2,
//     w: 12,
//     h: 1,
//   },
//   {
//     id: 'time',
//     x: 0,
//     y: 3,
//     w: 12,
//     h: 1,
//   },
// ];

function BookingPageItem({
  bookable,
  hideEndTime,
  timeThreshold = 30,
  forcedTimeThreshold = 30,
  hideName,
  hideUnbookedName,
  hideExtra,
  hideTime,
  hideLocation,
  hideImage,
  inverted = false,
  paddingLeft,
  paddingTop,
  paddingRight,
  paddingBottom,
  textAlignVertical,
  textAlignHorizontal,
}: BookingPageItemProps) {
  const [bookings, setBookings] = useState<BookingDisplay[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [fetchedBookable, setFetchedBookable] = useState<Bookable>();
  const { bookingNameFontSize, bookingUnbookedNameFontSize, bookingExtraFontSize, bookingLocationFontSize, bookingTimeFontSize, bookingPlacement: themePlacement } = useContext(BookingSvgThemeContext);
  const { cardBackground, bookingPrimaryForeground } = useContext(ThemePaletteContext);
  const nameLayout = svgLayout(bookingNameFontSize, 100);
  const unbookedNameLayout = svgLayout(bookingUnbookedNameFontSize, 100);
  const extraLayout = svgLayout(bookingExtraFontSize, 40);
  const locationLayout = svgLayout(bookingLocationFontSize, 40);
  const timeLayout = svgLayout(bookingTimeFontSize, 40);
  const shouldHideUnbookedName = hideUnbookedName ?? false;
  const containerStyle = inverted ? { backgroundColor: bookingPrimaryForeground, color: cardBackground } : undefined;
  const textStyle = inverted ? { color: cardBackground } : undefined;
  const svgTextStyle = inverted ? { fill: cardBackground } : undefined;
  const effTextAlignVertical = textAlignVertical ?? themePlacement.textAlignVertical ?? 'center';
  const effTextAlignHorizontal = textAlignHorizontal ?? themePlacement.textAlignHorizontal ?? 'center';
  const verticalJustify = { top: 'flex-start', center: 'center', bottom: 'flex-end' }[effTextAlignVertical] ?? 'center';
  const textAnchorProps =
    effTextAlignHorizontal === 'left'
      ? { x: '0', textAnchor: 'start' as const }
      : effTextAlignHorizontal === 'right'
        ? { x: '100%', textAnchor: 'end' as const }
        : { x: '50%', textAnchor: 'middle' as const };
  const placementStyle = { display: 'flex' as const, flexDirection: 'column' as const, justifyContent: verticalJustify };
  const offsetX = (settingNumber(paddingLeft) ?? themePlacement.paddingLeft ?? 0) - (settingNumber(paddingRight) ?? themePlacement.paddingRight ?? 0);
  const offsetY = (settingNumber(paddingTop) ?? themePlacement.paddingTop ?? 0) - (settingNumber(paddingBottom) ?? themePlacement.paddingBottom ?? 0);
  const textOffsetStyle = offsetX || offsetY ? { transform: `translate(${offsetX}px, ${offsetY}px)` } : undefined;
  const textLineStyle = textStyle || textOffsetStyle ? { ...(textStyle ?? {}), ...(textOffsetStyle ?? {}) } : undefined;
  const [blockEl, setBlockEl] = useState<HTMLDivElement | null>(null);
  const [textEl, setTextEl] = useState<HTMLDivElement | null>(null);
  const layoutKey = [
    verticalJustify,
    offsetX,
    offsetY,
    hideName,
    hideExtra,
    hideLocation,
    hideTime,
    hideEndTime,
    bookingNameFontSize,
    bookingExtraFontSize,
    bookingLocationFontSize,
    bookingTimeFontSize,
  ].join('|');
  const imageBand = useBookingImageBand(blockEl, textEl, offsetY, layoutKey);
  // An <img> with a missing or failing source renders a broken-image icon and border,
  // so only render it for a real URL and drop it again if that URL fails to load.
  const [failedImageUrl, setFailedImageUrl] = useState<string>();

  async function getBookable() {
    try {
      const result = await getBookableDisplay({bookableId: bookable?.bookableId ?? 0})
      setFetchedBookable((result as any)?.data)
    }
    catch (error) {}
  }

  async function getBookings() {
    try {
      const response = await getBookingDisplay({ bookableId: bookable?.bookableId ?? 0 });
      setBookings((response as any)?.data);
    } catch (error) {}
  }

  useEffect(() => {
    getBookings();
    getBookable();

    const interval = setInterval(() => {
      getBookings();
    }, 24 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const currentBooking = useMemo(() => {
    const timeThresholdMs = timeThreshold * 60 * 1000; // 30 minutes in milliseconds
    const forcedTimeThresholdMs = forcedTimeThreshold * 60 * 1000; // 30 minutes in milliseconds

    const bookingsInsideThreshold = bookings?.filter(({ startTime }) => {
      const startTimeMs = +new Date(formatDate(startTime.toString())); // Parse UTC start time
      return startTimeMs - new Date().getTime() <= timeThresholdMs;
    });

    const sortedBookings = bookingsInsideThreshold.sort((a, b) => {
      const aStartTime = +new Date(formatDate(a.startTime.toString()));
      const bStartTime = +new Date(formatDate(b.startTime.toString()));

      if (aStartTime - bStartTime > forcedTimeThresholdMs) {
        return 1;
      }

      return -1;
    });

    return sortedBookings?.[0] ?? null;
  }, [bookings, timeThreshold, forcedTimeThreshold, currentTime]);

  const bookingImageUrl = currentBooking?.imageUrl?.trim() || undefined;

  if (!currentBooking) {
    return !shouldHideUnbookedName ? (
      <div className="booking-block w-full h-full relative p-6" style={{ ...placementStyle, ...(containerStyle ?? {}) }}>
        <div className="booking-unbooked-name w-full" style={textLineStyle}>
          <svg width="100%" height="100%" viewBox={unbookedNameLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text {...textAnchorProps} y={unbookedNameLayout.y} dominantBaseline="middle" fontSize={bookingUnbookedNameFontSize} style={svgTextStyle}>
              {fetchedBookable?.name}
            </text>
          </svg>
        </div>
      </div>
    ) : null;
  }

  return (
    <div ref={setBlockEl} className="booking-block w-full h-full relative p-6" style={{ ...placementStyle, ...(containerStyle ?? {}) }}>
      <div ref={setTextEl} className="booking-text" style={textLineStyle}>
        <div className="booking-name">
          {!hideName && (
            <svg width="100%" height="100%" viewBox={nameLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
              <text {...textAnchorProps} y={nameLayout.y} dominantBaseline="middle" fontSize={bookingNameFontSize} style={svgTextStyle}>
                {currentBooking?.name ?? 'Namn'}
              </text>
            </svg>
          )}
        </div>
        <div className="booking-extra">
          {!hideExtra && (
            <svg width="100%" height="100%" viewBox={extraLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
              <text {...textAnchorProps} y={extraLayout.y} dominantBaseline="middle" fontSize={bookingExtraFontSize} style={svgTextStyle}>
                {currentBooking?.extra}
              </text>
            </svg>
          )}
        </div>
        <div className="booking-location">
          {!hideLocation && (
            <svg width="100%" height="100%" viewBox={locationLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
              <text {...textAnchorProps} y={locationLayout.y} dominantBaseline="middle" fontSize={bookingLocationFontSize} style={svgTextStyle}>
                {currentBooking?.location}
              </text>
            </svg>
          )}
        </div>
        <div className="booking-time">
          <svg width="100%" height="100%" viewBox={timeLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text {...textAnchorProps} y={timeLayout.y} dominantBaseline="middle" fontSize={bookingTimeFontSize} style={svgTextStyle}>
              {!hideTime && (
                <>
                  {formateTime(new Date(formatDate(currentBooking?.startTime?.toString())).toString())}
                  {hideEndTime ? '' : <> - {formateTime(new Date(formatDate(currentBooking?.endTime?.toString())).toString())}</>}
                </>
              )}
            </text>
          </svg>
        </div>
      </div>
      {!hideImage && bookingImageUrl && bookingImageUrl !== failedImageUrl && imageBand >= MIN_IMAGE_BAND && (
        <div className="booking-image absolute bottom-6 right-6" style={{ width: '33.3333%', height: imageBand }}>
          <img
            src={bookingImageUrl}
            onError={() => setFailedImageUrl(bookingImageUrl)}
            alt=""
            className="w-full h-full"
            style={{ objectFit: 'contain', objectPosition: 'bottom right' }}
          />
        </div>
      )}
    </div>
  );
}

export default BookingPageItem;
